import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import image1 from 'src/assets/Home.webp';
import image2 from 'src/assets/about.webp';
import image3 from 'src/assets/Fund manager.png';
import { 
  TrendingUp, 
  PieChart, 
  Shield, 
  FileText, 
  Target, 
  Users,
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  ArrowRight,
  Download,
  Calendar,
  ChevronDown,
  Award, 
  Briefcase, 
  GraduationCap
} from 'lucide-react';
import { fundData } from '../mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  TrendingUp,
  PieChart,
  Shield,
  FileText,
  Target,
  Users
};

const LandingPage = () => {
  const [activeSection, setActiveSection] = useState('home');
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    investmentAmount: ''
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(sectionId);
    }
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        toast.success(response.data.message);
        setFormData({ name: '', email: '', phone: '', message: '', investmentAmount: '' });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Something went wrong. Please try again or email us directly.');
    }
  };

  const handleConsultation = async () => {
    toast.success('Consultation request received! Please fill the contact form below with your details.');
    // Scroll to contact section
    scrollToSection('contact');
  };

  const handleDownload = () => {
    toast.info('Please contact us to receive the fund prospectus. Fill the form below!');
    scrollToSection('contact');
  };

  return (
    <div className="landing-page">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-transparent'
      }`}>
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <TrendingUp className={`w-8 h-8 ${isScrolled ? 'text-emerald-600' : 'text-white'}`} />
              <span className={`text-xl font-bold ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                Fortune Hands
              </span>
            </div>
            
            <nav className="hidden md:flex items-center gap-8">
              {[
                { id: 'home', label: 'Home' },
                { id: 'about', label: 'About' },
                { id: 'features', label: 'Features' },
                { id: 'strategy', label: 'Strategy' },
                { id: 'team', label: 'Our Team' },
                { id: 'faq', label: 'FAQ' },
                { id: 'contact', label: 'Contact' }
              ].map((section, index) => (
                <React.Fragment key={section.id}>
                  <button
                    onClick={() => scrollToSection(section.id)}
                    className={`text-sm font-medium transition-colors whitespace-nowrap ${
                      isScrolled ? 'text-gray-700 hover:text-emerald-600' : 'text-white/90 hover:text-white'
                    }`}
                  >
                    {section.label}
                  </button>
                  {index < 6 && (
                    <span className={`text-xs ${isScrolled ? 'text-gray-300' : 'text-white/30'}`}>|</span>
                  )}
                </React.Fragment>
              ))}
            </nav>

            <Button 
              onClick={handleConsultation}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section 
        id="home" 
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${image1})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="container mx-auto px-6 py-32 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in">
              {fundData.hero.title}
            </h1>
            <p className="text-xl md:text-2xl mb-4 text-emerald-300 font-semibold">
              {fundData.hero.subtitle}
            </p>
            <p className="text-lg md:text-xl mb-12 text-gray-200 max-w-3xl mx-auto">
              {fundData.hero.description}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
              <Button 
                size="lg" 
                onClick={handleConsultation}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-lg px-8 py-6 group"
              >
                <Calendar className="w-5 h-5 mr-2" />
                {fundData.hero.cta1}
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={handleDownload}
                className="bg-white/10 backdrop-blur-sm border-white text-white hover:bg-white hover:text-gray-900 text-lg px-8 py-6"
              >
                <Download className="w-5 h-5 mr-2" />
                {fundData.hero.cta2}
              </Button>
            </div>

            {/* Stats */}
            {/* <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-20">
              {fundData.stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-emerald-400 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm md:text-base text-gray-300">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div> */}
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-white" />
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                  {fundData.about.title}
                </h2>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  {fundData.about.description}
                </p>
                <div className="space-y-4">
                  {fundData.about.highlights.map((highlight, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{highlight}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative">
                <img 
                  src={image2}
                  alt="Professional Investment"
                  className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
                  loading="lazy"
                />
                <div className="absolute -bottom-6 -left-6 bg-emerald-600 text-white p-6 rounded-xl shadow-xl">
                  <div className="text-3xl font-bold">SEBI</div>
                  <div className="text-sm">Registered AIF</div>
                </div>
              </div>
            </div>
            
            {/* Fund Governance Section */}
            <div className="mt-20">
              <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">Professional Fund Governance</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {fundData.fundGovernance.map((gov, index) => (
                  <Card key={index} className="border-2 border-emerald-100 hover:border-emerald-300 transition-colors">
                    <CardHeader>
                      <CardTitle className="text-lg text-emerald-600">{gov.role}</CardTitle>
                      <CardDescription className="text-base font-semibold text-gray-900 mt-2">
                        {gov.entity}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600">{gov.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Choose Fortune Hands
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our commitment to excellence and investor success sets us apart
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {fundData.features.map((feature, index) => {
              const Icon = iconMap[feature.icon];
              return (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <CardHeader>
                    <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="w-7 h-7 text-emerald-600" />
                    </div>
                    <CardTitle className="text-xl">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base text-gray-600">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Investment Strategy */}
      <section id="strategy" className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-6 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-[2.0rem] md:text-5xl font-bold text-gray-900 mb-4">
                {fundData.investmentStrategy.title}
              </h2>
              <h2 className="text-[1.5rem] md:text-3xl font-bold text-gray-800 mb-4">Fortune Hands Growth Fund - Scheme I</h2> 
              <p className="text-xl text-gray-600 max-w-3xl mx-auto text-justify md:text-center">
                {fundData.investmentStrategy.description}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {fundData.investmentStrategy.strategies.map((strategy, index) => (
                <Card key={index} className="border-2 border-gray-200 hover:border-emerald-500 transition-colors duration-300">
                  <CardHeader>
                    <CardTitle className="text-2xl text-gray-900">{strategy.title}</CardTitle>
                    <CardDescription className="text-lg font-semibold text-emerald-600">
                      {strategy.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{strategy.details}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
       </div>
        </div>
      </section>

            {/* Performance Section */}
            {/* <div className="mt-20">
              <Card className="border-none shadow-xl bg-gradient-to-br from-emerald-50 to-white">
                <CardHeader className="text-center">
                  <CardTitle className="text-3xl">{fundData.performance.title}</CardTitle>
                  <CardDescription className="text-sm italic mt-2">{fundData.performance.subtitle}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {fundData.performance.metrics.map((metric, index) => (
                      <div key={index} className="bg-white p-6 rounded-lg shadow-sm border border-emerald-100">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                          <div className="flex-1">
                            <h4 className="text-lg font-semibold text-gray-900 mb-2">{metric.metric}</h4>
                            <p className="text-sm text-gray-600">{metric.note}</p>
                          </div>
                          <div className="flex-shrink-0 text-right">
                            <div className="text-3xl font-bold text-emerald-600 mb-1">
                              {metric.fundReturn}
                            </div>
                            <div className="text-sm text-gray-500">{metric.benchmark}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-8 grid md:grid-cols-2 gap-4">
                    {fundData.performance.highlights.map((highlight, index) => (
                      <div key={index} className="flex items-start space-x-3 p-4 bg-emerald-50 rounded-lg">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">{highlight}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div> */}


       <section id="team" className="fund-manager-section">
        <div className="section-container">
          
          {/* Section Header */}
          <div className="section-header">
            <div className="header-badge">
              <Award className="badge-icon" />
              <span>Leadership Excellence</span>
            </div>
            <h2 className="section-title" data-testid="section-title">Fund Manager Expertise</h2>
            <p className="section-subtitle">
              Guided by experience. Driven by performance. Committed to your financial growth.
            </p>
          </div>

        {/* Main Content Grid */}
        <div className="manager-content-grid">
          {/* Left Column - Image & Stats */}
          <div className="manager-visual-column">
            <div className="image-container" data-testid="manager-image-container">
              <div className="image-frame">
                <img 
                  src={image3}
                  alt="Fund Manager - CA Pawan Kumar Garg"
                  className="manager-image"
                  loading="lazy"
                />
                <div className="image-overlay">
                  <div className="overlay-pattern"></div>
                </div>
              </div>
              
              {/* Floating Stats Card */}
              <div className="floating-stats-card" data-testid="stats-card">
                {fundData.team.stats.map((stat, index) => (
                  <div key={index} className="stat-item">
                    <span className="stat-value">{stat.value}</span>
                    <span className="stat-label">{stat.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Info */}
          <div className="manager-info-column">
            {/* Name & Position */}
            <div className="manager-header" data-testid="manager-header">
              <h3 className="manager-name">{fundData.team.name}</h3>
              <p className="manager-position">{fundData.team.position}</p>
              <p className="manager-tagline">{fundData.team.tagline}</p>
            </div>

            {/* Bio */}
            <div className="manager-bio" data-testid="manager-bio">
              <p>{fundData.team.bio}</p>
            </div>

            {/* Credentials Grid */}
            <div className="credentials-grid">
              {/* Education */}
              <div className="credential-card" data-testid="education-card">
                <div className="credential-header">
                  <GraduationCap className="credential-icon" />
                  <h4>Education</h4>
                </div>
                <ul className="credential-list">
                  {fundData.team.education.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>

              {/* Certifications */}
              <div className="credential-card" data-testid="certifications-card">
                <div className="credential-header">
                  <Award className="credential-icon" />
                  <h4>Certifications</h4>
                </div>
                <ul className="credential-list">
                  {fundData.team.certifications.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Fund Information Cards */}
        {/* <div className="fund-info-section" data-testid="fund-info-section">
          <h4 className="fund-info-title">Fund Overview</h4>
          <div className="fund-info-grid">
            {[
              { icon: Briefcase, title: "Fund Manager", desc: "Sarvshakti Financial Advisors Private Limited" },
              { icon: Calendar, title: "Inception", desc: "December 2024" },
              { icon: Target, title: "Investment Horizon", desc: "5-7 Year Close-Ended Fund" },
              { icon: Users, title: "Diversification", desc: "13+ High-Growth Sectors" }
            ].map((item, index) => (
              <div key={index} className="fund-card">
                <div className="fund-card-icon">
                  <item.icon className="icon" />
                </div>
                <div className="fund-card-content">
                  <h5>{item.title}</h5>
                  <p>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div> */}
      </div>
    </section>

            


            {/* Sector Allocation Section */}
      <section id="strategy" className="sector-allocation">
            <div className="container mx-auto px-6 max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Sectoral Diversification</h3>
                <p className="text-lg text-gray-600">Strategic allocation across 13+ high-growth sectors</p>
              </div>
              <Card className="border-none shadow-xl">
                <CardContent className="p-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ">
                    {fundData.sectorAllocation.map((sector, index) => (
                      <div key={index} className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 bg-gray-50 rounded-lg hover:bg-emerald-50 transition-colors">
                        <span className="text-gray-700 font-medium">{sector.sector}</span>
                        <div className="flex items-center gap-3">
                          <div className="w-full md:w-32 bg-gray-200 rounded-full h-2.5">
                            <div 
                              className="bg-emerald-600 h-2.5 rounded-full transition-all duration-500" 
                              style={{ width: `${sector.percentage}%` }}
                            />
                          </div>
                          <span className="text-emerald-600 font-bold text-lg w-12 text-right">{sector.percentage}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>    
                    


   

      {/* FAQ Section */}
      <section id="faq" className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-gray-600">
                Everything you need to know about investing with us
              </p>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {fundData.faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-white border border-gray-200 rounded-lg px-6"
                >
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-emerald-600">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* testimonial */}
      {/* <section id="testimonial" className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Testimonials
              </h2>
              <p className="text-xl text-gray-600">
                Our Marquee Investors
              </p>
            </div>

            <div className="mt-20">              
              <div className="grid md:grid-cols-3 gap-8">
              {fundData.testimonials.map((testimonials, index) => (
                <Card key={index} className="border-2 border-gray-200 hover:border-emerald-500 transition-colors duration-300">
                  <CardHeader>
                    <img src={testimonials.image} alt="A description of the image" class="round-image"/>
                    <CardDescription className=" font-bolder text-emerald-600">
                      {testimonials.name}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{testimonials.testimonial}</p>
                  </CardContent>
                </Card>
              ))}
              </div>
            </div>
           
          </div>
        </div>
      </section>
    */}
    
      {/* Contact Section */}
      <section id="contact" className="py-16 md:py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                {fundData.contact.title}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {fundData.contact.description}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12">
              {/* Contact Info */}
              <div className="space-y-8">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Get in Touch</h3>
                  <div className="space-y-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-emerald-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900 mb-1">Address</div>
                        <div className="text-gray-600 whitespace-pre-line">
                          <span>901 D-MALL Netaji Subhash Place, Pitampura,
                            <br className="hidden md:block"/>
                            <span className="md:hidden"> </span>
                            New delhi - 110085
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="w-6 h-6 text-emerald-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900 mb-1">Email</div>
                        <a href={`mailto:${fundData.contact.email}`} className="text-emerald-600 hover:underline">
                          {fundData.contact.email}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Phone className="w-6 h-6 text-emerald-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900 mb-1">Phone</div>
                        <a href={`tel:${fundData.contact.phone}`} className="text-emerald-600 hover:underline">
                          {fundData.contact.phone}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <Card className="border-none shadow-xl">
                <CardHeader>
                  <CardTitle className="text-2xl">Send us a Message</CardTitle>
                  <CardDescription>Fill out the form and we'll get back to you shortly</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input 
                        id="name" 
                        name="name"
                        value={formData.name}
                        onChange={handleFormChange}
                        placeholder="Enter your name"
                        required
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input 
                        id="email" 
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleFormChange}
                        placeholder="your.email@example.com"
                        required
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input 
                        id="phone" 
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleFormChange}
                        placeholder="+91 XXXXX XXXXX"
                        required
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="investmentAmount">Proposed Investment Amount</Label>
                      <Input 
                        id="investmentAmount" 
                        name="investmentAmount"
                        value={formData.investmentAmount}
                        onChange={handleFormChange}
                        placeholder="₹ 1 Crore+"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message">Message</Label>
                      <Textarea 
                        id="message" 
                        name="message"
                        value={formData.message}
                        onChange={handleFormChange}
                        placeholder="Tell us about your investment goals..."
                        rows={4}
                        className="mt-1"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                      size="lg"
                    >
                      Submit Inquiry
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-12 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <TrendingUp className="w-8 h-8 text-emerald-500" />
                <span className="text-xl font-bold">Fortune Hands</span>
              </div>
              <p className="text-gray-400">
                SEBI Registered AIF Category II fund focused on long-term wealth creation through strategic growth investments.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                {['About', 'Features', 'Strategy', 'FAQ', 'Contact'].map((link) => (
                  <li key={link}>
                    <button 
                      onClick={() => scrollToSection(link.toLowerCase())}
                      className="text-gray-400 hover:text-emerald-500 transition-colors"
                    >
                      {link}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-gray-400">
                <li>SEBI Registration: IN/AIF2/24-25/1639</li>
                <li>SCORES ID: AIFN00188</li>
                <li className="text-sm mt-4">
                  © {new Date().getFullYear()} Fortune Hands Growth Fund
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8">
            <p className="text-sm text-gray-500 text-center">
              Disclaimer: Investments in securities market are subject to market risks. Read all the related documents carefully before investing. 
              Past performance is not indicative of future returns. Please consider your specific investment requirements before choosing a fund.
              The Site is for informational purposes only and does not constitute an offer to sell or a solicitation of an offer to buy any securities.
            </p><br></br>
            <p className="text-sm text-gray-500 text-center">
              The site provides information on our AIF Strategies solely for accredited investor, HNIs, Institutions and Professional under SEBI AIF Regulations,
              2012. It is not an offer to sell units or investment advice. AIF investments are high-risk, illiquid and are suitable only for sophisticated investors 
              aware of potential capital loss. 
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
